import { useMemo, useRef, useState, type UIEvent } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Play } from 'lucide-react';
import { useTheme } from '@course-authoring/shared-types';
import type { ScenarioOptionsPart } from '../../api/authorWithAiApi';
import { authorWithAiKeys } from '../../api/authorWithAiQueries';
import { listConceptVideos } from '../../api/centerstageApi';
import { FALLBACK_LIST_HEIGHT, LOAD_MORE_THRESHOLD_PX, PREVIEW_ROWS } from './optionsListConstants';
import { formatDuration } from './VideoPicker';
import { VideoPreview, type PreviewVideo } from './VideoPreview';
import { PICKER_COPY } from './authorWithAiCopy';
import { createAuthorWithAiStyles } from './authorWithAiStyles';

/**
 * A Centerstage concept's scenarios inside the thread: the first five in
 * Centerstage's order (newest first), then "Show all N" expanding in place
 * into a list of the same height that pages through the concept's videos.
 * A row opens the preview, and "Use this one" there picks the scenario.
 * Rows are active only when `onPick` is given (the latest assistant message).
 */

const PAGE_SIZE = 25;

interface Row {
  libraryId: string;
  title: string;
  description?: string;
  durationSeconds?: number;
  thumbnailUrl?: string;
  url?: string;
}

export interface ScenarioOptionsListProps {
  part: ScenarioOptionsPart;
  suggestion?: { category: string; reason: string };
  onPick?: (libraryId: string) => void;
  disabled?: boolean;
  /** Server rejections keyed by video id. */
  rowErrors?: Readonly<Record<string, string>>;
}

export function ScenarioOptionsList({ part, suggestion, onPick, disabled = false, rowErrors = {} }: ScenarioOptionsListProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
  const [showAll, setShowAll] = useState(false);
  const [listHeight, setListHeight] = useState<number | null>(null);
  const [atEnd, setAtEnd] = useState(false);
  const [preview, setPreview] = useState<PreviewVideo | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const full = useInfiniteQuery({
    queryKey: [...authorWithAiKeys.all, 'concept-videos', part.conceptId],
    queryFn: ({ pageParam }) => listConceptVideos(part.conceptId, pageParam, PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((total, current) => total + current.videos.length, 0);
      return lastPage.videos.length > 0 && fetched < lastPage.total ? allPages.length + 1 : undefined;
    },
    staleTime: 60 * 1000,
    enabled: showAll,
  });

  const fullRows: Row[] | undefined = full.data?.pages.flatMap((page) => page.videos).map((video) => ({
    libraryId: video.id,
    title: video.title,
    url: video.url,
    ...(video.description ? { description: video.description } : {}),
    ...(video.duration !== undefined ? { durationSeconds: video.duration } : {}),
    ...(video.thumbnailUrl ? { thumbnailUrl: video.thumbnailUrl } : {}),
  }));
  // Builds saved before 2026-11-01 may carry a hasCaptions flag on their items; it is ignored.
  const rows: Row[] = showAll ? (fullRows ?? part.items) : part.items.slice(0, PREVIEW_ROWS);
  const loadedAll = showAll && !full.hasNextPage && Boolean(full.data);
  const showCue = showAll && !(loadedAll && atEnd);

  const revealAll = () => {
    setListHeight(listRef.current?.offsetHeight || FALLBACK_LIST_HEIGHT);
    setShowAll(true);
    setAtEnd(false);
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const remaining = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (remaining < LOAD_MORE_THRESHOLD_PX && full.hasNextPage && !full.isFetchingNextPage) void full.fetchNextPage();
    setAtEnd(remaining <= 1);
  };

  /** Opens at once with the row; the preview loads the player and the rest from the video detail. */
  const openPreview = (row: Row) => {
    setPreview({
      id: row.libraryId,
      title: row.title,
      ...(row.url ? { url: row.url } : {}),
      ...(row.description ? { description: row.description } : {}),
      ...(row.durationSeconds !== undefined ? { duration: row.durationSeconds } : {}),
      ...(row.thumbnailUrl ? { thumbnailUrl: row.thumbnailUrl } : {}),
    });
  };

  const use = (videoId: string) => {
    if (disabled) return;
    setPreview(null);
    onPick?.(videoId);
  };

  return (
    <div style={styles.questionOptions} data-testid="scenario-options">
      <div
        style={{ ...styles.pickScroll, ...(showAll ? { height: `${listHeight ?? FALLBACK_LIST_HEIGHT}px`, overflowY: 'auto' } : {}) }}
        onScroll={showAll ? handleScroll : undefined}
        data-testid="scenario-options-list"
      >
        <ul ref={listRef} style={styles.pickList}>
          {rows.map((row) => {
            const duration = formatDuration(row.durationSeconds);
            const error = rowErrors[row.libraryId];
            const content = (
              <>
                <span style={{ ...styles.poster, display: 'block', cursor: 'inherit' }}>
                  {row.thumbnailUrl && <img src={row.thumbnailUrl} alt="" style={styles.posterImage} />}
                  {(onPick || (suggestion && !row.thumbnailUrl)) && <span style={styles.playBadge} aria-hidden="true"><Play size="1em" strokeWidth={1.75} aria-hidden="true" /></span>}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span>{row.title}</span>
                  {(duration || suggestion) && (
                    <span style={styles.pickMeta}>
                      {suggestion
                        ? <span>{[duration, suggestion.category, part.label].filter(Boolean).join(' · ')}</span>
                        : duration && <span>{duration}</span>}
                    </span>
                  )}
                  {row.description && <span style={styles.scenarioDescription}>{row.description}</span>}
                  {suggestion && <span style={styles.scenarioDescription}>{suggestion.reason}</span>}
                  {error && <span style={{ ...styles.error, display: 'block' }} role="alert">{error}</span>}
                </span>
              </>
            );
            return (
              <li key={row.libraryId}>
                {onPick
                  ? (
                    <button
                      type="button"
                      style={styles.pickRow}
                      onClick={() => openPreview(row)}
                      disabled={disabled}
                    >
                      {content}
                    </button>
                  )
                  : <div style={{ ...styles.pickRow, ...styles.pickRowInert }}>{content}</div>}
              </li>
            );
          })}
        </ul>
        {showCue && <div style={styles.fade} aria-hidden="true" />}
      </div>
      {showCue && <span style={styles.scrollCue}>{PICKER_COPY.scrollForMore}</span>}
      {full.isError && <p style={styles.error} role="alert">{PICKER_COPY.loadError}</p>}
      {onPick && !showAll && part.total > PREVIEW_ROWS && (
        <button type="button" style={styles.showMore} onClick={revealAll}>{PICKER_COPY.showAll(part.total)}</button>
      )}
      {onPick && (
        <VideoPreview
          video={preview}
          busy={disabled}
          onClose={() => setPreview(null)}
          onUse={use}
        />
      )}
    </div>
  );
}
